import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { mutationEditorClear } from 'gql/mutations/editor';
import { useMutationChapterUpdate } from 'gql/mutations/chapter';
import { useQueryLocalEditor } from 'gql/queries/local';
import extract from 'helpers/extract';
import { trackEvent, reportError } from 'lib/tracking';
import { notify } from 'uikit/Notifications';

import ChapterEdit from './ChapterEdit';

const ChapterEditContainer = (props) => {
  const { t } = useTranslation();
  const {
    data: {
      editor: { status: editorStatus, chapterEditing },
    },
  } = useQueryLocalEditor();
  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        title: yup
          .string()
          .trim()
          .required(t('Course/Teacher/Editor/ChapterEdit/title-error-required'))
          .max(
            256,
            t('Course/Teacher/Editor/ChapterEdit/title-error-required'),
          ),
        isOffline: yup.boolean(),
        chooseDate: yup.boolean(),
        onlineTimestamp: yup.date().nullable(),
        offlineTimestamp: yup.date().when('onlineTimestamp', {
          is: (val) => val === null,
          then: yup.date().nullable(),
          otherwise: yup
            .date()
            .nullable()
            .min(
              yup.ref('onlineTimestamp'),
              t(
                'Course/Teacher/Editor/ChapterEdit/offline-timestamp-error-after-online-timestamp',
              ),
            ),
        }),
        isLinked: yup.boolean(),
      }),
    [t],
  );
  const mutationChapterUpdate = useMutationChapterUpdate();
  const handleSave = useCallback(
    async (values, form) => {
      try {
        form.setStatus(null);
        const variables = extract(values, validationSchema);
        const { data } = await mutationChapterUpdate(chapterEditing, variables);
        notify(
          'success',
          t(
            data.chapterUpdate.isOffline
              ? 'Course/Teacher/Editor/ChapterEdit/chapter-offline'
              : data.chapterUpdate.isHidden
              ? 'Course/Teacher/Editor/ChapterEdit/chapter-hidden'
              : 'Course/Teacher/Editor/ChapterEdit/chapter-online',
          ),
        );
        mutationEditorClear();
        trackEvent('chapter', 'update');
      } catch (err) {
        form.setStatus('500');
        reportError('chapter', 'update', err);
      }
    },
    [chapterEditing, mutationChapterUpdate, validationSchema, t],
  );
  const initialValues = useMemo(
    () =>
      chapterEditing
        ? {
            title: '',
            isLinked: false,
            studentChoice: !chapterEditing.isLinked,
            ...chapterEditing,
            chooseDate:
              (!!chapterEditing.onlineTimestamp ||
                !!chapterEditing.offlineTimestamp) &&
              !chapterEditing.isOffline,
          }
        : {},
    [chapterEditing],
  );
  const isModalOpen =
    editorStatus === 'chapter-create' || editorStatus === 'chapter-update';
  const [isEditingTitle, setEditingTitle] = useState(false);
  const handleTitleEditToggle = useCallback(() => {
    setEditingTitle((v) => !v);
  }, []);
  useEffect(() => {
    if (!isModalOpen) {
      setEditingTitle(false);
    }
  }, [isModalOpen]);
  const handleIsOfflineChange = useCallback(({ setFieldValue, values }) => {
    if (values.chooseDate && !values.isOffline) {
      setFieldValue('chooseDate', false);
    }
    setFieldValue('isOffline', !values.isOffline);
  }, []);
  const handleChooseDateChange = useCallback(({ setFieldValue, values }) => {
    if (!values.chooseDate && values.isOffline) {
      setFieldValue('isOffline', false);
    }
    setFieldValue('chooseDate', !values.chooseDate);
  }, []);
  return (
    <ChapterEdit
      handleChooseDateChange={handleChooseDateChange}
      handleClose={mutationEditorClear}
      handleIsOfflineChange={handleIsOfflineChange}
      handleSave={handleSave}
      handleTitleEditToggle={handleTitleEditToggle}
      initialValues={initialValues}
      isEditingTitle={isEditingTitle}
      isModalOpen={isModalOpen}
      t={t}
      validationSchema={validationSchema}
      {...props}
    />
  );
};

export default ChapterEditContainer;
